import { externalLinks } from './site'
import { welcomeWeekVisible } from './welcomeWeek'

// SPECIAL (date-specific) EVENTS — the only time-sensitive content on the site.
//
// HOW TO MAINTAIN THIS FILE
// • Add an entry only once BCM leadership confirms it.
// • Leave `visible: false` on anything still being planned; hidden entries are
//   filtered out before rendering, so drafts never reach the page.
// • Omit `time`, `location`, or `date` rather than guessing — the card simply
//   renders fewer lines.
// Recurring weekly activities live in data/recurring.ts, not here.

export type EventStatus = 'Confirmed' | 'Details pending' | 'Draft'

export type BcmEvent = {
  id: string
  category: 'Fall 2026' | 'Weekly' | 'Coming Soon'
  title: string
  /** Human readable date or schedule label. */
  date: string
  /** Leave undefined when a time has not been confirmed. */
  time?: string
  /** Short location line (used in compact cards). */
  location?: string
  /** Fuller location sentence (used on the Events page). */
  locationDetail?: string
  /** Confirmed location split across lines for card display. */
  locationLines?: string[]
  /** Shown in place of a location when details are still pending. */
  locationNote?: string
  description: string
  /** Extra note shown under the details. */
  clarification?: string
  /** Optional verified external call to action (the event's destination). */
  cta?: { label: string; href: string }
  /** Optional verified external reference link. */
  reference?: { label: string; href: string }
  /** Internal maintenance status. Never rendered. */
  status: EventStatus
  /** Gate for public display. */
  visible: boolean
  /** Sortable key. Undated entries sort last. */
  sortKey?: string
}

const specialEvents: BcmEvent[] = [
  {
    id: 'first-worship-night-fall-2026',
    category: 'Fall 2026',
    title: 'First Worship Night',
    date: 'Thursday, August 27, 2026',
    time: '7:30 PM',
    location: 'Cape Florida Ballroom, Room 316',
    locationDetail: 'Cape Florida Ballroom, Room 316 · UCF Student Union, 3rd Floor',
    locationLines: ['Cape Florida Ballroom, Room 316', 'UCF Student Union, 3rd Floor'],
    description:
      'Join UCF BCM for the first worship night of the fall semester. Chick-fil-A will be served after the service.',
    cta: { label: 'Get Updates', href: externalLinks.getConnectedForm },
    status: 'Confirmed',
    visible: true,
    sortKey: '2026-08-27',
  },
  // The confirmed Welcome Week schedule lives in data/welcomeWeek.ts and renders
  // as its own seasonal section, so it is not duplicated here.
]

/** The only events any public component may render. */
export const visibleSpecialEvents: BcmEvent[] = specialEvents.filter((event) => event.visible)

// While Welcome Week is running the bar promotes it; setting
// `welcomeWeekVisible` to false in data/welcomeWeek.ts returns it to the
// regular first-worship-night message with no other edits.
export const announcement = welcomeWeekVisible
  ? ({
      message: 'Welcome Week • August 24–28 • Events every night!',
      linkLabel: 'See the Schedule',
      linkTo: '/events',
    } as const)
  : ({
      message: 'First Worship Night: Thursday, August 27 at 7:30 PM',
      linkLabel: 'View Events',
      linkTo: '/events',
    } as const)
